import { useEffect, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import Nav from '../components/Nav';
import Footer from '../components/Footer';
import { supabase } from '../lib/supabase';
import { formatDate, formatTime } from '../lib/format';

type InviteView = {
  invite_id: string;
  email: string;
  rsvp_status: string;
  booking_id: string;
  booking_date: string;
  booking_time: string;
  headcount: number;
  vendor_name: string;
  vendor_address: string | null;
  offer_title: string;
  organizer_name: string | null;
};

type Response = 'yes' | 'no' | 'maybe';

export default function InviteRespond() {
  const { token } = useParams();
  const [params] = useSearchParams();
  const presetResponse = params.get('respond') as Response | null;

  const [data, setData] = useState<InviteView | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [submitting, setSubmitting] = useState<Response | null>(null);
  const autoAppliedRef = useRef(false);

  async function load() {
    if (!token) return;
    const { data, error } = await supabase.rpc('ga_invite_by_token', { p_token: token });
    if (error || !data || data.length === 0) {
      setNotFound(true);
      return;
    }
    setData(data[0]);
  }

  async function respond(response: Response) {
    if (!token) return;
    setSubmitting(response);
    await supabase.rpc('ga_rsvp_by_token', { p_token: token, p_response: response });
    setSubmitting(null);
    await load();
  }

  useEffect(() => { load(); }, [token]);

  // One-click RSVP from email: auto-apply ?respond=yes / no / maybe once data is loaded.
  useEffect(() => {
    if (!data || autoAppliedRef.current) return;
    if (presetResponse === 'yes' || presetResponse === 'no' || presetResponse === 'maybe') {
      autoAppliedRef.current = true;
      respond(presetResponse);
    }
  }, [data, presetResponse]);

  if (notFound) {
    return (
      <>
        <Nav />
        <div style={{ padding: '120px 0', textAlign: 'center' }}>
          <h1 className="display" style={{ fontSize: 'clamp(40px, 6vw, 56px)' }}>This invite link isn't valid.</h1>
          <p className="muted">Maybe the organizer revoked it? Ask them for a fresh one.</p>
          <Link to="/" className="btn btn-ghost" style={{ marginTop: 24 }}>← Go home</Link>
        </div>
        <Footer />
      </>
    );
  }

  if (!data) {
    return (
      <>
        <Nav />
        <div style={{ padding: '120px 0', textAlign: 'center', color: 'var(--ink-soft)' }}>Loading invite…</div>
      </>
    );
  }

  const wasAutoApplied = autoAppliedRef.current && (data.rsvp_status === presetResponse);

  return (
    <>
      <Nav />
      <section style={{ paddingTop: 48, paddingBottom: 96 }}>
        <div className="container-narrow" style={{ maxWidth: 620 }}>
          {wasAutoApplied && (
            <div style={{ marginBottom: 24, padding: 18, borderRadius: 14, background: 'var(--card)' }}>
              <div style={{ fontWeight: 600, marginBottom: 4 }}>
                {presetResponse === 'yes' ? "You're in." : presetResponse === 'no' ? "Got it. We will let the organizer know." : 'Logged as a maybe.'}
              </div>
              <div className="muted" style={{ fontSize: 14 }}>You can change your response any time on this page.</div>
            </div>
          )}

          <div className="eyebrow" style={{ marginBottom: 18 }}>You're invited</div>
          <h1 className="display" style={{ fontSize: 'clamp(40px, 6.5vw, 64px)', margin: 0, lineHeight: 1 }}>
            {data.organizer_name ? data.organizer_name.split(' ')[0] : 'Your team'} planned a happy hour at{' '}
            <span className="serif-italic" style={{ color: 'var(--wine)' }}>{data.vendor_name}.</span>
          </h1>

          <div style={{ marginTop: 28, padding: 24, borderRadius: 18, background: 'var(--card)' }}>
            <Row k="When" v={`${formatDate(data.booking_date)}, ${formatTime(data.booking_time)}`} />
            <Row k="Where" v={`${data.vendor_name}${data.vendor_address ? ', ' + data.vendor_address : ''}`} />
            <Row k="Package" v={data.offer_title} />
            <Row k="Group" v={`${data.headcount} people`} />
            <Row k="Your status" v={<span className={`tag ${data.rsvp_status === 'yes' ? 'tag-active' : data.rsvp_status === 'no' ? 'tag-cancelled' : 'tag-pending'}`}>{data.rsvp_status}</span>} />
          </div>

          <div style={{ marginTop: 28 }}>
            <div className="field-label">Can you make it?</div>
            <div className="invite-cta-row">
              <button onClick={() => respond('yes')} className="btn btn-pop" disabled={!!submitting}>
                {submitting === 'yes' ? '...' : "I'm in →"}
              </button>
              <button onClick={() => respond('maybe')} className="btn btn-ghost" disabled={!!submitting}>
                {submitting === 'maybe' ? '...' : 'Maybe'}
              </button>
              <button onClick={() => respond('no')} className="btn btn-ghost" disabled={!!submitting}>
                {submitting === 'no' ? '...' : "Can't make it"}
              </button>
            </div>
          </div>

          <p className="muted" style={{ marginTop: 32, fontSize: 14 }}>
            You don't need an account to RSVP. Your response is tied to this private link.
          </p>
        </div>
      </section>
      <Footer />

      <style>{`
        .invite-cta-row { margin-top: 12px; display: flex; gap: 12px; flex-wrap: wrap; }
        @media (max-width: 720px) {
          .invite-cta-row { flex-direction: column; align-items: stretch; }
          .invite-cta-row .btn { width: 100%; justify-content: center; }
        }
      `}</style>
    </>
  );
}

function Row({ k, v }: { k: string; v: React.ReactNode }) {
  return (
    <div className="row-between" style={{ padding: '8px 0' }}>
      <span className="muted">{k}</span>
      <span>{v}</span>
    </div>
  );
}
